import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import subscriptionService, { Plan } from '../../services/subscriptionService';

const UpgradeSubscription: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlanParam = searchParams.get('plan');

  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>(selectedPlanParam || 'professional');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const availablePlans = await subscriptionService.getAvailablePlans();
      setPlans(availablePlans.filter((p) => p.plan !== 'trial'));
    } catch (err) {
      setError('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPlan = () => {
    return plans.find((p) => p.plan === selectedPlan);
  };

  const getAmount = () => {
    const plan = getCurrentPlan();
    if (!plan) return 0;
    return billingCycle === 'annual' ? plan.pricing.annual : plan.pricing.monthly;
  };

  const handleUpgrade = async () => {
    setProcessing(true);
    setError('');

    try {
      const subscription = await subscriptionService.createSubscription({
        plan: selectedPlan as any,
        billingCycle,
        autoRenew: true,
      });

      alert(`Subscription created!

In production, this would open the payment gateway.

Subscription ID: ${subscription._id}
Amount: ${subscriptionService.formatCurrency(getAmount())}
Plan: ${subscriptionService.formatPlanName(selectedPlan)}

Redirecting to subscription management...`);

      setTimeout(() => {
        navigate('/subscription');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create subscription');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="mt-2 text-sm text-gray-600">Loading plans...</p>
        </div>
      </div>
    );
  }

  // Map plan names to display names matching landing page
  const getPlanDisplayName = (planName: string) => {
    const nameMap: Record<string, string> = {
      'starter': 'Small Business',
      'professional': 'Growing Business',
      'enterprise': 'Enterprise'
    };
    return nameMap[planName] || planName;
  };

  const getPlanDescription = (planName: string) => {
    const descMap: Record<string, string> = {
      'starter': 'Perfect for single location retailers',
      'professional': 'For multi-location businesses',
      'enterprise': 'For large distributors & manufacturers'
    };
    return descMap[planName] || '';
  };

  const getDisplayFeatures = (planName: string) => {
    const plan = plans.find(p => p.plan === planName);
    if (!plan) return [];

    const baseFeatures = [
      `${plan.limits.maxUsers === -1 ? 'Unlimited' : plan.limits.maxUsers} Users`,
      `${plan.limits.maxInventoryItems === -1 ? 'Unlimited' : plan.limits.maxInventoryItems.toLocaleString()} Inventory Items`,
      `${plan.limits.maxWarehouses === -1 ? 'Unlimited' : plan.limits.maxWarehouses} ${plan.limits.maxWarehouses === 1 ? 'Warehouse' : 'Warehouses'}`,
    ];

    if (planName === 'starter') {
      return [
        ...baseFeatures,
        'Unlimited POs & Invoices',
        'Retail Billing Module',
        'Basic Reports',
        'Email + Phone Support'
      ];
    } else if (planName === 'professional') {
      return [
        ...baseFeatures,
        'Unlimited Everything',
        'Multi-Warehouse Support',
        'Advanced Reports & Analytics',
        'Priority Support',
        'Barcode Scanning',
        'API Access'
      ];
    } else if (planName === 'enterprise') {
      return [
        ...baseFeatures,
        'Dedicated Account Manager',
        'Custom Feature Development',
        '24/7 Premium Support',
        'On-premise Deployment Option',
        'Training & Onboarding Sessions'
      ];
    }
    return baseFeatures;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/subscription')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Subscription
          </button>
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Choose Your Plan</h1>
            <p className="text-xl text-gray-600 mb-2">Select the plan that fits your business needs</p>
            <p className="text-gray-500">Starting at just ₹299/month - less than ₹10 per day!</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex rounded-lg border-2 border-gray-300 p-1 bg-white shadow-sm">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-8 py-3 rounded-md text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Monthly Billing
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-8 py-3 rounded-md text-sm font-medium transition-all ${
                billingCycle === 'annual'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Annual Billing
              <span className={`ml-2 text-xs font-bold ${billingCycle === 'annual' ? 'text-green-300' : 'text-green-600'}`}>
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards - Horizontal Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {plans.map((plan) => {
            const isSelected = selectedPlan === plan.plan;
            const isFeatured = plan.plan === 'professional';
            const monthlyPrice = plan.pricing.monthly;
            const annualPrice = plan.pricing.annual;
            const displayPrice = billingCycle === 'annual' ? annualPrice : monthlyPrice;
            const features = getDisplayFeatures(plan.plan);

            return (
              <div
                key={plan.plan}
                onClick={() => setSelectedPlan(plan.plan)}
                className={`relative bg-white rounded-2xl shadow-xl cursor-pointer transition-all duration-200 hover:shadow-2xl ${
                  isSelected ? 'ring-4 ring-indigo-600 ring-offset-2 transform scale-105' : 'hover:transform hover:scale-105'
                } ${isFeatured ? 'border-4 border-indigo-600' : 'border-2 border-gray-200'}`}
              >
                {isFeatured && (
                  <div className="absolute -top-5 left-0 right-0 flex justify-center">
                    <span className="bg-indigo-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      ⭐ MOST POPULAR
                    </span>
                  </div>
                )}

                <div className={`p-8 ${isFeatured ? 'pt-10' : 'pt-8'}`}>
                  {/* Plan Name & Description */}
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {getPlanDisplayName(plan.plan)}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {getPlanDescription(plan.plan)}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="mb-6 pb-6 border-b border-gray-200">
                    {plan.plan === 'enterprise' ? (
                      <>
                        <div className="text-5xl font-bold text-gray-900 mb-2">Custom</div>
                        <p className="text-sm text-gray-600">Contact us for pricing</p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline mb-2">
                          <span className="text-5xl font-bold text-gray-900">
                            ₹{displayPrice.toLocaleString()}
                          </span>
                          <span className="ml-2 text-lg text-gray-600">
                            /{billingCycle === 'annual' ? 'year' : 'month'}
                          </span>
                        </div>
                        {billingCycle === 'annual' && monthlyPrice * 12 > annualPrice && (
                          <p className="text-sm font-semibold text-green-600">
                            Save ₹{(monthlyPrice * 12 - annualPrice).toLocaleString()} per year
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-4 mb-8">
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Select Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPlan(plan.plan);
                    }}
                    className={`w-full py-4 px-6 rounded-lg font-bold text-lg transition-all shadow-md hover:shadow-lg ${
                      isSelected
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                        : isFeatured
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {isSelected ? '✓ Selected' : plan.plan === 'enterprise' ? 'Contact Sales' : 'Select Plan'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Proceed Button Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Ready to get started?</h3>
            <p className="text-gray-600">
              {selectedPlan === 'enterprise'
                ? 'Contact our sales team for custom pricing and features'
                : `You'll be charged ${subscriptionService.formatCurrency(getAmount())} ${billingCycle === 'annual' ? 'annually' : 'monthly'}`
              }
            </p>
          </div>

          <div className="flex justify-center mb-6">
            <button
              onClick={handleUpgrade}
              disabled={processing || selectedPlan === 'enterprise'}
              className={`px-12 py-5 rounded-lg font-bold text-xl transition-all shadow-xl ${
                selectedPlan === 'enterprise'
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-2xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
            >
              {processing
                ? 'Processing...'
                : selectedPlan === 'enterprise'
                  ? 'Contact Sales Team'
                  : `Proceed to Payment - ${subscriptionService.formatCurrency(getAmount())}`
              }
            </button>
          </div>

          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-600 mb-4">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <span className="font-medium">Secure payment</span>
            </div>
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <span className="font-medium">24/7 support</span>
            </div>
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <span className="font-medium">Cancel anytime</span>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center">
            By proceeding, you agree to our{' '}
            <a href="#" className="text-indigo-600 hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-indigo-600 hover:underline">Privacy Policy</a>
          </p>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">Frequently Asked Questions</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Can I change my plan later?</h4>
              <p className="text-sm text-gray-600">Yes, you can upgrade or downgrade your plan anytime from your subscription settings.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">What payment methods do you accept?</h4>
              <p className="text-sm text-gray-600">We accept all major credit/debit cards, UPI, net banking, and digital wallets.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Is my data secure?</h4>
              <p className="text-sm text-gray-600">Yes, we use bank-level encryption and daily backups to keep your data completely secure.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Do you offer refunds?</h4>
              <p className="text-sm text-gray-600">Yes, we offer a 30-day money-back guarantee if you're not satisfied with our service.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeSubscription;
